import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Image,
  Alert,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { 
  FaUserEdit, FaCamera, FaKey, FaUser, FaEnvelope, 
  FaPhoneAlt, FaClock, FaCalendarAlt, FaShieldAlt, FaCrown, 
  FaUserMd, FaTruck, FaClipboard, FaCheckCircle, FaLock, 
  FaSave, FaTimes, FaSyncAlt
} from 'react-icons/fa';
import { get, put, checkPermissions, BASE_URL } from '../utils/api';
import { usePermissions } from '../utils/PermissionContext';
import { useToast } from './ToastContext';
import { formatPermissionName } from '../utils/permissionUtils';
import './Profile.css';
import ChangePasswordForm from './ChangePasswordForm';
import { DEFAULT_PROFILE_IMAGE } from '../config';

const IMAGE_BASE_URL = BASE_URL;

const getRoleBadge = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'admin':
      return {
        icon: <FaCrown className="me-1" />,
        label: 'System Administrator',
        className: 'role-badge-admin'
      };
    case 'pharmacist':
      return {
        icon: <FaUserMd className="me-1" />,
        label: 'Licensed Pharmacist',
        className: 'role-badge-pharmacist'
      };
    case 'delivery':
      return {
        icon: <FaTruck className="me-1" />,
        label: 'Delivery Personnel',
        className: 'role-badge-delivery'
      };
    default:
      return {
        icon: <FaUser className="me-1" />,
        label: 'Retail Customer',
        className: 'role-badge-customer'
      };
  }
};

const Profile = () => {
  const { permissions: allPermissions } = usePermissions();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    profileImage: DEFAULT_PROFILE_IMAGE,
    role: '',
    last_login: null,
    created_at: null,
    user_id: null,
  });

  const [imageFile, setImageFile] = useState(null);
  const [directPermissions, setDirectPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get('/auth/profile');
      if (response && response.success && response.data) {
        let avatarUrl = response.data.profile_image_url || response.data.profile_image || response.data.avatar || null;
        if (avatarUrl && typeof avatarUrl === 'string' && avatarUrl.startsWith('/')) {
          avatarUrl = `${IMAGE_BASE_URL}${avatarUrl}`;
        }

        setFormData({
          name: response.data.name || '',
          email: response.data.email || '',
          mobile: response.data.mobile || '',
          profileImage: avatarUrl || DEFAULT_PROFILE_IMAGE,
          role: response.data.role || '',
          last_login: response.data.last_login || null,
          created_at: response.data.created_at || null,
          user_id: response.data.user_id || null,
        });

        if (response.data.user_id) {
          const permResponse = await checkPermissions(response.data.user_id);
          if (permResponse && permResponse.success && permResponse.permissions) {
            setDirectPermissions(permResponse.permissions.direct || []);
            setRolePermissions(permResponse.permissions.from_role || []);
            if (permResponse.role) {
              setFormData(prev => ({ ...prev, role: permResponse.role }));
            }
          }
        }
      }
    } catch (error) {
      showToast('Failed to load profile details.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, profileImage: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);

    const profileData = new FormData();
    profileData.append('name', formData.name);
    profileData.append('mobile', formData.mobile);
    if (imageFile) {
      profileData.append('profileImage', imageFile);
    }

    try {
      const response = await put('/auth/profile', profileData);
      if (response && response.success) {
        setIsEditing(false);
        setImageFile(null);
        showToast('Profile updated successfully!', 'success');
        fetchProfile();
      } else {
        showToast(response?.message || 'Profile update failed.', 'danger');
      }
    } catch (error) {
      showToast(error.message || 'Failed to update profile.', 'danger');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCopy = async (text, label = 'Value') => {
    try {
      if (!navigator?.clipboard) throw new Error('Clipboard not available');
      await navigator.clipboard.writeText(text || '');
      showToast(`${label} copied to clipboard`, 'success');
    } catch {
      showToast(`Failed to copy ${label}`, 'danger');
    }
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = DEFAULT_PROFILE_IMAGE;
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    try {
      const dt = new Date(date);
      if (isNaN(dt.getTime())) return 'N/A';
      return dt.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'N/A';
    }
  };

  const roleMeta = getRoleBadge(formData.role);

  return (
    <Container fluid className="profile-mgmt-page px-3 px-lg-4 py-3">
      {/* Profile Hero Header Card */}
      <div className="profile-hero-panel mb-4 p-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-4">
          <div className="d-flex align-items-center gap-4 flex-wrap flex-sm-nowrap">
            {/* Avatar with Camera Overlay */}
            <div className="profile-avatar-container">
              <Image 
                src={formData.profileImage} 
                className="profile-hero-avatar" 
                onError={handleImageError} 
              />
              <label className="profile-camera-badge" title="Change Profile Avatar">
                <FaCamera />
                <input 
                  type="file" 
                  hidden 
                  onChange={handleImageChange} 
                  accept="image/png, image/jpeg, image/gif" 
                />
              </label>
            </div>

            {/* Title & Info */}
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h2 className="profile-user-name mb-0">{formData.name || 'User Profile'}</h2>
                <span className="profile-id-pill">ID #{formData.user_id || '—'}</span>
              </div>
              
              <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                <span className={`profile-role-pill ${roleMeta.className}`}>
                  {roleMeta.icon} {roleMeta.label}
                </span>
                <span className="profile-verified-badge">
                  <FaCheckCircle className="me-1 text-success" /> Active Account
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button 
              variant={isEditing ? "secondary" : "primary"} 
              size="sm" 
              onClick={() => {
                setIsEditing(!isEditing);
                setShowPasswordSection(false);
              }}
              className="profile-action-btn"
            >
              {isEditing ? <><FaTimes className="me-1" /> Cancel Edit</> : <><FaUserEdit className="me-1" /> Edit Profile</>}
            </Button>

            <Button 
              variant={showPasswordSection ? "secondary" : "outline-primary"} 
              size="sm" 
              onClick={() => {
                setShowPasswordSection(!showPasswordSection);
                setIsEditing(false);
              }}
              className="profile-action-btn"
            >
              <FaKey className="me-1" /> {showPasswordSection ? 'Hide Security' : 'Change Password'}
            </Button>

            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={fetchProfile} 
              className="profile-action-btn"
              title="Refresh profile"
            >
              <FaSyncAlt className={loading ? 'fa-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Grid: Account Info & Capabilities */}
      <Row className="g-4">
        {/* Left Column: Personal Data & Credentials */}
        <Col lg={7}>
          {isEditing ? (
            <div className="profile-card-box p-4 rounded glassmorphic-panel mb-4">
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                <FaUserEdit className="text-primary fs-5" />
                <h5 className="profile-card-title mb-0">Edit Profile Details</h5>
              </div>

              <Form onSubmit={handleFormSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Full Legal Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    className="profile-form-input"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Email Address (Read-Only)</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    className="profile-form-input readonly-input"
                  />
                  <Form.Text className="text-muted small">
                    Email address is tied to your account login credentials.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="profile-form-label">Mobile Contact Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="e.g. 9876543210"
                    className="profile-form-input"
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                  <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={savingProfile}>
                    Cancel
                  </Button>
                  <Button variant="success" type="submit" disabled={savingProfile} className="profile-save-btn fw-bold px-4">
                    {savingProfile ? <><Spinner animation="border" size="sm" className="me-1" /> Saving...</> : <><FaSave className="me-1" /> Save Changes</>}
                  </Button>
                </div>
              </Form>
            </div>
          ) : (
            <div className="profile-card-box p-4 rounded glassmorphic-panel mb-4">
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                <FaUser className="text-primary fs-5" />
                <h5 className="profile-card-title mb-0">Personal & Account Information</h5>
              </div>

              <div className="profile-info-grid">
                {/* Full Name */}
                <div className="profile-info-row p-3 rounded mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="profile-item-label">Full Name</span>
                    <span className="profile-item-val">{formData.name || 'Not provided'}</span>
                  </div>
                </div>

                {/* Email */}
                <div className="profile-info-row p-3 rounded mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="profile-item-label">
                      <FaEnvelope className="me-1 text-primary" /> Email Address
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="profile-item-val">{formData.email || 'Not provided'}</span>
                      {formData.email && (
                        <button 
                          type="button" 
                          className="profile-copy-btn" 
                          onClick={() => handleCopy(formData.email, 'Email address')}
                          title="Copy email"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mobile */}
                <div className="profile-info-row p-3 rounded mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="profile-item-label">
                      <FaPhoneAlt className="me-1 text-info" /> Mobile Number
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="profile-item-val">{formData.mobile || 'Not provided'}</span>
                      {formData.mobile && (
                        <button 
                          type="button" 
                          className="profile-copy-btn" 
                          onClick={() => handleCopy(formData.mobile, 'Mobile number')}
                          title="Copy phone number"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="profile-meta-card p-3 rounded">
                      <span className="profile-meta-label">
                        <FaClock className="me-1 text-warning" /> Last Login
                      </span>
                      <span className="profile-meta-val d-block mt-1">
                        {formatDateTime(formData.last_login)}
                      </span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile-meta-card p-3 rounded">
                      <span className="profile-meta-label">
                        <FaCalendarAlt className="me-1 text-success" /> Registered Since
                      </span>
                      <span className="profile-meta-val d-block mt-1">
                        {formatDateTime(formData.created_at)}
                      </span>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Change Password Card */}
          {showPasswordSection && (
            <div className="profile-password-wrapper mb-4">
              <ChangePasswordForm 
                onPasswordChangeSuccess={() => {
                  setShowPasswordSection(false);
                  showToast('Password changed successfully!', 'success');
                }} 
                onCancel={() => setShowPasswordSection(false)} 
              />
            </div>
          )}
        </Col>

        {/* Right Column: Roles & Capabilities */}
        <Col lg={5}>
          {/* Role Status Card */}
          <div className="profile-card-box p-4 rounded glassmorphic-panel mb-4">
            <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
              <FaShieldAlt className="text-info fs-5" />
              <h5 className="profile-card-title mb-0">Role & Security Authority</h5>
            </div>

            <div className="profile-role-overview p-3 rounded mb-3 text-center">
              <div className="role-avatar-circle mx-auto mb-2">
                {roleMeta.icon}
              </div>
              <h5 className="text-white fw-bold mb-1">{roleMeta.label}</h5>
              <p className="text-muted small mb-0">Assigned Organizational Role</p>
            </div>

            <div className="profile-stat-item p-3 rounded mb-2 d-flex justify-content-between align-items-center">
              <span className="text-muted small">Authority Scope:</span>
              <span className="text-white fw-semibold small">
                {formData.role === 'admin' ? 'Superadmin Root Authority' : 'Assigned Role Permissions'}
              </span>
            </div>
          </div>

          {/* Granted Permissions List */}
          <div className="profile-card-box p-4 rounded glassmorphic-panel">
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <FaLock className="text-success fs-5" />
                <h5 className="profile-card-title mb-0">Granted Capabilities</h5>
              </div>
              <Badge bg="primary" className="px-2 py-1">
                {rolePermissions.length + directPermissions.length} Active
              </Badge>
            </div>

            {formData.role === 'admin' ? (
              <div className="admin-root-banner p-3 rounded text-center">
                <FaCrown className="text-warning fs-3 mb-2" />
                <h6 className="text-white fw-bold mb-1">Full Root System Privileges</h6>
                <p className="text-muted small mb-0">
                  As an Administrator, your account has complete, unrestricted access across all application modules, user accounts, and billing ledgers.
                </p>
              </div>
            ) : rolePermissions.length > 0 || directPermissions.length > 0 ? (
              <div className="profile-perms-container">
                {rolePermissions.length > 0 && (
                  <div className="mb-3">
                    <span className="profile-perm-heading">Inherited from Role ({formData.role})</span>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {rolePermissions.map(p => (
                        <span key={p.permission_id} className="profile-perm-chip">
                          <FaCheckCircle className="me-1 text-info small" />
                          {formatPermissionName(p.permission_name)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {directPermissions.length > 0 && (
                  <div>
                    <span className="profile-perm-heading">Directly Assigned Permissions</span>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {directPermissions.map(p => (
                        <span key={p.permission_id} className="profile-perm-chip direct">
                          <FaCheckCircle className="me-1 text-success small" />
                          {formatPermissionName(p.permission_name)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-muted small">
                No specific permissions mapped to this profile.
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
